/**
 * Debug trace page for admin pipeline inspection.
 * Accessible via ?debug URL parameter (admin only).
 *
 * Layout:
 * - Header: title + back link
 * - Search: GID input
 * - Two-panel: left = state inspector, right = event timeline
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  activateTrace,
  deactivateTrace,
  fetchJourneyState,
  type JourneyState,
  type TraceActivation,
} from '../services/debugApi';
import { useTraceStream } from '../hooks/useTraceStream';
import type { TraceEvent } from '../hooks/useTraceStream';

// =============================================================================
// Stage colours
// =============================================================================

const STAGE_COLORS: Record<string, string> = {
  'ingest.received': 'text-blue-400',
  'ingest.dedup': 'text-yellow-400',
  'v4.resolution': 'text-cyan-400',
  'kafka.produced': 'text-gray-400',
  'consumer.received': 'text-green-400',
  'db.written': 'text-green-400',
  'cache.updated': 'text-green-400',
  'v5.segment': 'text-purple-400',
  'ws.broadcast': 'text-purple-400',
};

function stageColor(stage: string | undefined): string {
  if (!stage) return 'text-gray-300';
  return STAGE_COLORS[stage] ?? 'text-gray-300';
}

// =============================================================================
// TTL Countdown
// =============================================================================

function TTLCountdown({ expiresAt }: { expiresAt: string }) {
  const [remaining, setRemaining] = useState<number>(0);

  useEffect(() => {
    const target = new Date(expiresAt).getTime();
    const tick = () => {
      const diff = Math.max(0, Math.round((target - Date.now()) / 1000));
      setRemaining(diff);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  const mins = Math.floor(remaining / 60);
  const secs = remaining % 60;
  const label = `${mins}m ${secs.toString().padStart(2, '0')}s`;
  const color = remaining < 60 ? 'text-red-400' : remaining < 300 ? 'text-yellow-400' : 'text-green-400';

  return <span className={`font-mono text-sm ${color}`}>{remaining > 0 ? label : 'expired'}</span>;
}

// =============================================================================
// State Panel
// =============================================================================

interface StatePanelProps {
  journeyGid: string;
  state: JourneyState | null;
  loading: boolean;
  traceInfo: TraceActivation | null;
  connected: boolean;
  onActivate: () => void;
  onDeactivate: () => void;
  onRefresh: () => void;
}

function StatePanel({
  journeyGid,
  state,
  loading,
  traceInfo,
  connected,
  onActivate,
  onDeactivate,
  onRefresh,
}: StatePanelProps) {
  const isTracing = state?.trace_active ?? false;

  return (
    <div className="w-80 flex-shrink-0 flex flex-col gap-3 overflow-y-auto pr-1">
      {/* Journey ID */}
      <div className="bg-gray-800 rounded-lg p-3">
        <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Journey GID</div>
        <div className="font-mono text-sm text-gray-100 break-all">{journeyGid || '—'}</div>
        {state?.journey_uuid && (
          <div className="text-xs text-gray-500 font-mono mt-1 break-all">{state.journey_uuid}</div>
        )}
      </div>

      {/* Trace controls */}
      <div className="bg-gray-800 rounded-lg p-3 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500 uppercase tracking-wider">Trace</span>
          <span
            className={`text-xs font-mono px-2 py-0.5 rounded ${
              connected
                ? 'bg-green-900 text-green-300'
                : isTracing
                ? 'bg-yellow-900 text-yellow-300'
                : 'bg-gray-700 text-gray-400'
            }`}
          >
            {connected ? 'streaming' : isTracing ? 'active' : 'inactive'}
          </span>
        </div>

        {traceInfo && isTracing && (
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <span>Expires:</span>
            <TTLCountdown expiresAt={traceInfo.expires_at} />
          </div>
        )}

        <div className="flex gap-2">
          {!isTracing ? (
            <button
              onClick={onActivate}
              disabled={loading || !journeyGid}
              className="flex-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-700 disabled:text-gray-500 text-white text-sm rounded transition-colors"
            >
              Start Trace
            </button>
          ) : (
            <button
              onClick={onDeactivate}
              disabled={loading}
              className="flex-1 px-3 py-1.5 bg-red-700 hover:bg-red-600 disabled:bg-gray-700 text-white text-sm rounded transition-colors"
            >
              Stop Trace
            </button>
          )}
          <button
            onClick={onRefresh}
            disabled={loading || !journeyGid}
            className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 text-gray-300 text-sm rounded transition-colors"
            title="Refresh state"
          >
            ↺
          </button>
        </div>
      </div>

      {/* Positions */}
      {state?.positions && Object.keys(state.positions).length > 0 && (
        <div className="bg-gray-800 rounded-lg p-3">
          <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">Positions</div>
          {Object.entries(state.positions).map(([key, pos]) => (
            <div key={key} className="mb-2 last:mb-0">
              <div className="text-xs text-blue-400 font-mono mb-0.5">{key}</div>
              <div className="text-xs font-mono text-gray-300">
                {pos.lat.toFixed(5)}, {pos.lng.toFixed(5)}
              </div>
              {pos.age_s !== null && (
                <div className="text-xs text-gray-500">age: {pos.age_s.toFixed(1)}s</div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* V5 Segment */}
      {state?.v5_segment && (
        <div className="bg-gray-800 rounded-lg p-3">
          <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">V5 Segment</div>
          <div className="space-y-1 text-xs font-mono">
            <div>
              <span className="text-gray-500">state: </span>
              <span className="text-purple-400">{state.v5_segment.state}</span>
            </div>
            {state.v5_segment.from_stop && (
              <div>
                <span className="text-gray-500">from: </span>
                <span className="text-gray-300">{state.v5_segment.from_stop}</span>
              </div>
            )}
            {state.v5_segment.to_stop && (
              <div>
                <span className="text-gray-500">to: </span>
                <span className="text-gray-300">{state.v5_segment.to_stop}</span>
              </div>
            )}
            {state.v5_segment.progress !== null && (
              <div>
                <span className="text-gray-500">progress: </span>
                <span className="text-gray-300">{(state.v5_segment.progress * 100).toFixed(1)}%</span>
              </div>
            )}
            {state.v5_segment.authority && (
              <div>
                <span className="text-gray-500">authority: </span>
                <span className="text-cyan-400">{state.v5_segment.authority}</span>
              </div>
            )}
            {state.v5_segment.delay_s !== null && (
              <div>
                <span className="text-gray-500">delay: </span>
                <span className={state.v5_segment.delay_s > 0 ? 'text-red-400' : 'text-green-400'}>
                  {state.v5_segment.delay_s}s
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Pipeline Health */}
      {state?.pipeline_health && (
        <div className="bg-gray-800 rounded-lg p-3">
          <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">Pipeline Health</div>
          <div className="space-y-1 text-xs font-mono">
            <div>
              <span className="text-gray-500">v4_ingests: </span>
              <span className="text-blue-400">{state.pipeline_health.v4_ingests}</span>
            </div>
            <div>
              <span className="text-gray-500">v1da_ingests: </span>
              <span className="text-purple-400">{state.pipeline_health.v1da_ingests}</span>
            </div>
            <div>
              <span className="text-gray-500">dedup_rejections: </span>
              <span className="text-yellow-400">{state.pipeline_health.dedup_rejections}</span>
            </div>
          </div>
        </div>
      )}

      {/* Timestamps */}
      {state?.timestamps && (
        <div className="bg-gray-800 rounded-lg p-3">
          <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">Timestamps</div>
          <div className="space-y-1 text-xs font-mono">
            {Object.entries(state.timestamps)
              .filter(([, v]) => v !== null)
              .map(([k, v]) => (
                <div key={k}>
                  <div className="text-gray-500">{k.replace('last_', '')}:</div>
                  <div className="text-gray-300 pl-2">{v as string}</div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Loading indicator */}
      {loading && (
        <div className="text-center text-gray-500 text-sm py-2">Loading...</div>
      )}
    </div>
  );
}

// =============================================================================
// Event Timeline
// =============================================================================

interface EventTimelineProps {
  events: TraceEvent[];
  onClear: () => void;
}

function EventTimeline({ events, onClear }: EventTimelineProps) {
  const [autoScroll, setAutoScroll] = useState(true);
  const bottomRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (autoScroll && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [events, autoScroll]);

  const handleScroll = () => {
    if (!containerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 50;
    setAutoScroll(isAtBottom);
  };

  return (
    <div className="flex-1 flex flex-col min-w-0">
      {/* Timeline header */}
      <div className="flex items-center justify-between mb-2 flex-shrink-0">
        <div className="text-xs text-gray-500 uppercase tracking-wider">
          Events{events.length > 0 ? ` (${events.length})` : ''}
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={autoScroll}
              onChange={(e) => setAutoScroll(e.target.checked)}
              className="w-3 h-3 accent-blue-500"
            />
            <span className="text-xs text-gray-400">Auto-scroll</span>
          </label>
          <button
            onClick={onClear}
            className="text-xs text-gray-500 hover:text-gray-300 transition-colors px-2 py-0.5 rounded border border-gray-700 hover:border-gray-500"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Event list */}
      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto bg-gray-800 rounded-lg p-2 font-mono text-xs space-y-1"
      >
        {events.length === 0 ? (
          <div className="text-gray-600 text-center py-8">
            No events yet. Start a trace and wait for pipeline activity.
          </div>
        ) : (
          events.map((ev) => (
            <div
              key={ev.id}
              className="flex gap-2 items-start hover:bg-gray-700 rounded px-1 py-0.5 transition-colors"
            >
              <span className="text-gray-600 flex-shrink-0 w-20 truncate" title={ev.ts}>
                {ev.ts ? new Date(ev.ts).toLocaleTimeString() : '??:??:??'}
              </span>
              <span className={`flex-shrink-0 w-32 truncate ${stageColor(ev.stage)}`} title={ev.stage}>
                {ev.stage ?? ev.event}
              </span>
              <span className="text-gray-400 flex-shrink-0 w-16 truncate" title={ev.source}>
                {ev.source ?? ''}
              </span>
              <span className="text-gray-300 truncate flex-1" title={ev.raw}>
                {ev.raw}
              </span>
            </div>
          ))
        )}
        <div ref={bottomRef} />
      </div>
    </div>
  );
}

// =============================================================================
// Main DebugTrace page
// =============================================================================

export function DebugTrace() {
  const [inputGid, setInputGid] = useState('');
  const [activeGid, setActiveGid] = useState('');
  const [journeyState, setJourneyState] = useState<JourneyState | null>(null);
  const [traceInfo, setTraceInfo] = useState<TraceActivation | null>(null);
  const [loading, setLoading] = useState(false);
  const [stateError, setStateError] = useState<string | null>(null);

  const isTracing = journeyState?.trace_active ?? false;

  const { events, connected, error: streamError, clearEvents } = useTraceStream({
    journeyGid: activeGid,
    enabled: isTracing,
  });

  const loadState = useCallback(async (gid: string) => {
    if (!gid) return;
    setLoading(true);
    setStateError(null);
    try {
      const state = await fetchJourneyState(gid);
      setJourneyState(state);
    } catch (err) {
      setStateError(err instanceof Error ? err.message : 'Failed to load state');
      setJourneyState(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleGo = useCallback(() => {
    const gid = inputGid.trim();
    if (!gid) return;
    setActiveGid(gid);
    setTraceInfo(null);
    clearEvents();
    void loadState(gid);
  }, [inputGid, loadState, clearEvents]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleGo();
  };

  const handleActivate = useCallback(async () => {
    if (!activeGid) return;
    setLoading(true);
    try {
      const result = await activateTrace(activeGid);
      setTraceInfo(result);
      await loadState(activeGid);
    } catch (err) {
      setStateError(err instanceof Error ? err.message : 'Failed to activate trace');
    } finally {
      setLoading(false);
    }
  }, [activeGid, loadState]);

  const handleDeactivate = useCallback(async () => {
    if (!activeGid) return;
    setLoading(true);
    try {
      await deactivateTrace(activeGid);
      setTraceInfo(null);
      await loadState(activeGid);
    } catch (err) {
      setStateError(err instanceof Error ? err.message : 'Failed to deactivate trace');
    } finally {
      setLoading(false);
    }
  }, [activeGid, loadState]);

  const handleRefresh = useCallback(() => {
    void loadState(activeGid);
  }, [activeGid, loadState]);

  return (
    <div className="h-screen flex flex-col bg-gray-900 text-gray-100 font-mono">
      {/* Header */}
      <header className="flex-shrink-0 flex items-center justify-between px-4 py-3 bg-gray-800 border-b border-gray-700">
        <div className="flex items-center gap-3">
          <a
            href="/"
            className="text-gray-400 hover:text-gray-200 text-sm transition-colors"
            onClick={(e) => {
              e.preventDefault();
              window.location.search = '';
            }}
          >
            ← Map
          </a>
          <span className="text-gray-600">|</span>
          <h1 className="text-base font-semibold text-gray-100">
            Journey Trace{' '}
            <span className="text-xs text-red-400 font-mono bg-red-900/30 px-1.5 py-0.5 rounded">
              [admin]
            </span>
          </h1>
        </div>
        {connected && (
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            <span className="text-xs text-green-400">streaming</span>
          </div>
        )}
      </header>

      {/* Search bar */}
      <div className="flex-shrink-0 px-4 py-3 bg-gray-850 border-b border-gray-700 flex gap-2">
        <input
          type="text"
          value={inputGid}
          onChange={(e) => setInputGid(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Enter journey GID (e.g. 9015014500100345)"
          className="flex-1 bg-gray-700 text-gray-100 placeholder-gray-500 px-3 py-2 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 font-mono"
        />
        <button
          onClick={handleGo}
          disabled={!inputGid.trim() || loading}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-700 disabled:text-gray-500 text-white text-sm rounded transition-colors"
        >
          Go
        </button>
      </div>

      {/* Error banner */}
      {(stateError ?? streamError) && (
        <div className="flex-shrink-0 px-4 py-2 bg-red-900/40 border-b border-red-800 text-red-300 text-sm">
          {stateError ?? streamError}
        </div>
      )}

      {/* Two-panel layout */}
      <div className="flex-1 flex gap-4 p-4 overflow-hidden min-h-0">
        <StatePanel
          journeyGid={activeGid}
          state={journeyState}
          loading={loading}
          traceInfo={traceInfo}
          connected={connected}
          onActivate={handleActivate}
          onDeactivate={handleDeactivate}
          onRefresh={handleRefresh}
        />
        <EventTimeline events={events} onClear={clearEvents} />
      </div>
    </div>
  );
}

export default DebugTrace;
